import { OrderStatus } from "./order";
import { SuratJalanStatus } from "./suratJalan";
import { UangJalanReportStatus } from "./uangJalanReport";

export type DashboardPreset =
  | "today"
  | "yesterday"
  | "this_week"
  | "last_week"
  | "this_month"
  | "last_month"
  | "custom";

export interface DashboardKpiValue {
  current: number;
  previous: number;
}

export interface DashboardExpenseTypeValue {
  paid: number;
  approved: number;
}

export type DashboardExpenseType =
  | "uang_jalan"
  | "mob"
  | "taktis"
  | "registration"
  | "additional"
  | "standby";

export interface DashboardSeriesPoint {
  date: string;
  orders: number;
  standbyOrders: number;
  totalPaid: number;
}

export interface DashboardTeamRow {
  teamOperationsId: string;
  name: string;
  outstandingBalance: number;
  ordersInPeriod: number;
  totalPaidInPeriod: number;
}

export interface DashboardPeriod {
  preset: DashboardPreset;
  from: string;
  to: string;
  timezone: string;
  compareFrom: string;
  compareTo: string;
}

export interface DashboardData {
  period: DashboardPeriod;
  actionRequired: {
    orders: number;
    standbyOrders: number;
    uangJalanReports: number;
    suratJalan: number;
  };
  kpis: {
    orders: DashboardKpiValue;
    standbyOrders: DashboardKpiValue;
    reports: DashboardKpiValue;
    totalPaid: DashboardKpiValue;
    totalApprovedCost: DashboardKpiValue;
    difference: DashboardKpiValue;
  };
  series: DashboardSeriesPoint[];
  expensesByType: Record<DashboardExpenseType, DashboardExpenseTypeValue>;
  ordersByStatus: Record<OrderStatus, number>;
  reportsByStatus: Record<UangJalanReportStatus, number>;
  suratJalanByStatus: Record<SuratJalanStatus, number>;
  teams: DashboardTeamRow[];
}

const ZERO_EXPENSE_TYPES = (): Record<DashboardExpenseType, DashboardExpenseTypeValue> => ({
  uang_jalan: { paid: 0, approved: 0 },
  mob: { paid: 0, approved: 0 },
  taktis: { paid: 0, approved: 0 },
  registration: { paid: 0, approved: 0 },
  additional: { paid: 0, approved: 0 },
  standby: { paid: 0, approved: 0 },
});

const zeroOrderStatuses = (): Record<OrderStatus, number> => ({
  [OrderStatus.OPEN]: 0,
  [OrderStatus.READY_FOR_REVIEW]: 0,
  [OrderStatus.IN_REVIEW]: 0,
  [OrderStatus.REVISION_REQUIRED]: 0,
  [OrderStatus.VERIFIED]: 0,
  [OrderStatus.APPROVED]: 0,
  [OrderStatus.APPROVED_WITH_OVERRIDE]: 0,
  [OrderStatus.COMPLETED]: 0,
});

const zeroSuratJalanStatuses = (): Record<SuratJalanStatus, number> => ({
  [SuratJalanStatus.IN_REVIEW]: 0,
  [SuratJalanStatus.INVOICING_REVIEW]: 0,
  [SuratJalanStatus.REVISION_REQUIRED]: 0,
  [SuratJalanStatus.APPROVED]: 0,
});

const zeroReportStatuses = (): Record<UangJalanReportStatus, number> => ({
  [UangJalanReportStatus.OPEN]: 0,
  [UangJalanReportStatus.IN_REVIEW]: 0,
  [UangJalanReportStatus.REVISION_REQUIRED]: 0,
  [UangJalanReportStatus.VERIFIED]: 0,
  [UangJalanReportStatus.APPROVED]: 0,
  [UangJalanReportStatus.APPROVED_WITH_OVERRIDE]: 0,
});

export const mapDashboardResponse = (response: { data?: DashboardData }): DashboardData => {
  const payload = response.data;
  if (!payload) {
    throw new Error("Invalid dashboard response");
  }

  const expensesByType = { ...ZERO_EXPENSE_TYPES(), ...(payload.expensesByType ?? {}) };
  const ordersByStatus = { ...zeroOrderStatuses(), ...(payload.ordersByStatus ?? {}) };
  const reportsByStatus = { ...zeroReportStatuses(), ...(payload.reportsByStatus ?? {}) };
  const suratJalanByStatus = {
    ...zeroSuratJalanStatuses(),
    ...(payload.suratJalanByStatus ?? {}),
  };

  return {
    ...payload,
    actionRequired: {
      orders: payload.actionRequired?.orders ?? 0,
      standbyOrders: payload.actionRequired?.standbyOrders ?? 0,
      uangJalanReports: payload.actionRequired?.uangJalanReports ?? 0,
      suratJalan: payload.actionRequired?.suratJalan ?? 0,
    },
    expensesByType,
    ordersByStatus,
    reportsByStatus,
    suratJalanByStatus,
    series: payload.series ?? [],
    teams: payload.teams ?? [],
  };
};
